import {
	SetInFollowedListDocument,
	SetInFollowedListMutation,
	SetInFollowedListMutationVariables,
} from '@/components/watchlist/graphql/mutations/SetInFollowedList.mutation';

import { getVm } from '@/helpers/vm-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';
import { useMutation } from '@/helpers/composables/useApollo';
import { toastError, toastSuccess } from '@/helpers/toast/toast-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';

export const MAX_LIST_NAME_LENGTH = 200;

export function getListRenameError(error: Error, listName: string) {
	const vm = getVm();

	return error.message.includes('name already taken') ? vm.$t('WEBAPP_LISTS_LIST_NAME_TAKEN', { listName }) : null;
}

export function getListCreateError(error: Error, listName: string) {
	const vm = getVm();
	const isPremium = vm.$store.getters['user/isPremium'];

	if (error.message.includes('maxCustomList')) {
		return vm.$t('WEBAPP_LISTS_CREATE_NEW_LIST_FAIL', {
			action: isPremium
				? vm.$t('WEBAPP_LISTS_CREATE_NEW_LIST_DELETE_FIRST')
				: vm.$t('WEBAPP_LISTS_CREATE_NEW_LIST_GET_PRO'),
		});
	}

	return getListRenameError(error, listName);
}

export async function toggleFollowList(
	{
		listId,
		isFollowed,
		listName,
	}: {
		listId: string;
		isFollowed: boolean;
		listName: string;
	},
	additionalContexts?: SnowplowContext[]
) {
	const vm = getVm();

	const preventAction = await preventNonSignedInAction(undefined, additionalContexts);

	if (preventAction || !listId) {
		return;
	}

	try {
		const message = isFollowed
			? vm.$t('WEBAPP_HASBEENREMOVEDFROMYOURLIBRARY', { listName })
			: vm.$t('WEBAPP_HASBEENADDEDTOYOURLIBRARY', { listName });
		const trackingValue = isFollowed ? 0 : 1;

		const { mutate, onError } = useMutation<SetInFollowedListMutation, SetInFollowedListMutationVariables>(
			SetInFollowedListDocument,
			{
				variables: {
					input: { id: listId, state: !isFollowed, name: listName },
				},
			}
		);

		await mutate();

		onError(error =>
			captureMessageForSentry(
				'[GraphQL Follow List Event]:',
				{ error, where: 'Composable: useFollowList' },
				'error'
			)
		);

		toastSuccess({ message: message as string });

		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'follow_content',
				label: 'list',
				property: listId,
				value: trackingValue,
			},
			additionalContexts
		);
	} catch (e) {
		toastError({ message: vm.$tc('WEBAPP_ALERT_ERROR_TITLE'), duration: 2000 });
	}
}
